import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X } from "lucide-react";

export const MAX_PICKUPS = 2;

// Editable list of pickup rows ({pharmacy_id, qty}); each pharmacy can be used once.
export function PickupRows({ rows, onChange, pharmacies, excludeIds = [], max = MAX_PICKUPS, testPrefix, allowNone = false }) {
  const update = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i) => onChange(rows.length > 1 ? rows.filter((_, idx) => idx !== i) : [{ pharmacy_id: "", qty: "" }]);
  const tid = (name, i) => `${testPrefix}-${name}${i === 0 ? "" : `-${i + 1}`}`;
  const canAdd = rows.length < max && rows.every((r) => r.pharmacy_id);

  return (
    <div className="space-y-2">
      {rows.map((r, i) => {
        const taken = new Set([...excludeIds, ...rows.filter((_, idx) => idx !== i).map((x) => x.pharmacy_id)]);
        const options = (pharmacies || []).filter((p) => !taken.has(p.id));
        return (
          <div key={i} className="grid grid-cols-[minmax(0,1fr)_76px_36px] gap-2 items-center" data-testid={tid("row", i)}>
            <Select value={r.pharmacy_id} onValueChange={(v) => update(i, { pharmacy_id: v === "__none__" ? "" : v })}>
              <SelectTrigger data-testid={tid("pharmacy", i)}>
                <SelectValue placeholder={i === 0 ? "Pick up packages at another pharmacy…" : "2nd pickup pharmacy…"} />
              </SelectTrigger>
              <SelectContent className="z-[9999]">
                {allowNone && i === 0 && <SelectItem value="__none__">— No pickup —</SelectItem>}
                {options.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.nickname ? `${p.nickname} · ${p.name}` : p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="number"
              min={1}
              placeholder="Qty"
              value={r.qty}
              onChange={(e) => update(i, { qty: e.target.value })}
              disabled={!r.pharmacy_id}
              data-testid={tid("qty", i)}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-muted-foreground hover:text-destructive"
              onClick={() => removeRow(i)}
              disabled={rows.length === 1 && !r.pharmacy_id}
              aria-label="Remove pickup"
              data-testid={tid("remove", i)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        );
      })}
      {canAdd && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-primary hover:text-primary px-2"
          onClick={() => onChange([...rows, { pharmacy_id: "", qty: "" }])}
          data-testid={`${testPrefix}-add-second`}
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Add 2nd pickup
        </Button>
      )}
    </div>
  );
}

export const toPickupPayload = (rows) =>
  rows.filter((r) => r.pharmacy_id).map((r) => ({ pharmacy_id: r.pharmacy_id, qty: r.qty ? Number(r.qty) : null }));
